import { TrendingUp, Package, ShoppingBag, Truck, AlertTriangle, Mail, Star, RefreshCcw } from "lucide-react";
import { Card, SectionTitle, StatusBadge, PRODUCT_NAMES, chf, fmtDate, Empty } from "./shared";

const Kpi = ({ icon: Icon, label, value, sub, accent, testId }) => (
  <Card className="p-5" data-testid={testId}>
    <div className="flex items-start justify-between">
      <Icon size={18} style={{ color: accent }} />
      {sub && <span className="text-[10px] font-mono text-stone-400">{sub}</span>}
    </div>
    <p className="mt-3 font-serif text-3xl text-stone-900">{value}</p>
    <p className="mt-1 text-[10px] font-mono uppercase tracking-[0.2em] text-stone-500">{label}</p>
  </Card>
);

const delta = (cur, prev) => {
  if (!prev) return cur ? "nouveau" : "—";
  const p = Math.round(((cur - prev) / prev) * 100);
  return `${p >= 0 ? "+" : ""}${p}% vs mois préc.`;
};

const Chart = ({ series }) => {
  const max = Math.max(...series.map((d) => d.revenue), 1);
  return (
    <div className="flex items-end gap-1 h-36" data-testid="revenue-chart">
      {series.map((d) => (
        <div key={d.date} className="flex-1 flex flex-col items-center justify-end h-full group relative">
          <div className="absolute -top-7 hidden group-hover:block rounded bg-stone-900 text-white text-[10px] px-2 py-1 font-mono whitespace-nowrap z-10">
            {d.date.slice(8)}/{d.date.slice(5, 7)} · {chf(d.revenue)} · {d.orders} cmd
          </div>
          <div className="w-full rounded-t bg-[#C85A32] transition-colors group-hover:bg-stone-900" style={{ height: `${Math.max((d.revenue / max) * 100, d.orders ? 4 : 1)}%`, opacity: d.orders ? 1 : 0.25 }} />
        </div>
      ))}
    </div>
  );
};

export const Overview = ({ stats, goTo }) => {
  if (!stats) return <Empty>Chargement…</Empty>;
  const m = stats.month;
  const ranking = Object.entries(stats.product_sales).sort((a, b) => b[1] - a[1]);
  return (
    <div className="space-y-10" data-testid="admin-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats">
        <Kpi icon={TrendingUp} label="CA du mois (TTC)" value={chf(m.revenue)} sub={delta(m.revenue, m.prev_revenue)} accent="#C85A32" testId="kpi-month-revenue" />
        <Kpi icon={ShoppingBag} label="Commandes du mois" value={m.orders} sub={delta(m.orders, m.prev_orders)} accent="#4A624E" testId="kpi-month-orders" />
        <Kpi icon={Package} label="Panier moyen" value={chf(m.avg_basket)} accent="#4A624E" testId="kpi-avg-basket" />
        <Kpi icon={Truck} label="À expédier" value={stats.pending_orders} sub="confirmées + en préparation" accent="#C85A32" testId="kpi-pending" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="p-6 lg:col-span-2">
          <SectionTitle>Chiffre d'affaires — 30 derniers jours</SectionTitle>
          <Chart series={stats.series} />
          <div className="mt-3 flex justify-between text-[10px] font-mono text-stone-400">
            <span>{stats.series[0].date.slice(5)}</span><span>{stats.series[stats.series.length - 1].date.slice(5)}</span>
          </div>
          <div className="mt-5 grid grid-cols-3 gap-4 border-t border-stone-100 pt-5 text-sm">
            <div><p className="text-[10px] font-mono uppercase tracking-wide text-stone-400">CA total</p><p className="font-mono text-stone-900">{chf(stats.revenue_total)}</p></div>
            <div><p className="text-[10px] font-mono uppercase tracking-wide text-stone-400">Commandes</p><p className="font-mono text-stone-900">{stats.orders_total}</p></div>
            <div><p className="text-[10px] font-mono uppercase tracking-wide text-stone-400">Remboursé</p><p className="font-mono text-stone-900">{chf(stats.refunded_total)}</p></div>
          </div>
        </Card>

        <div className="space-y-4">
          <Card className="p-5" data-testid="alerts-card">
            <SectionTitle>À traiter</SectionTitle>
            <ul className="space-y-3 text-sm">
              <li><button onClick={() => goTo("commandes")} className="flex w-full items-center justify-between hover:text-[#C85A32]"><span className="flex items-center gap-2"><Truck size={14} /> Commandes à expédier</span><b className="font-mono">{stats.pending_orders}</b></button></li>
              <li><button onClick={() => goTo("messages")} className="flex w-full items-center justify-between hover:text-[#C85A32]"><span className="flex items-center gap-2"><Mail size={14} /> Messages non lus</span><b className="font-mono">{stats.messages_unread}</b></button></li>
              <li><button onClick={() => goTo("avis")} className="flex w-full items-center justify-between hover:text-[#C85A32]"><span className="flex items-center gap-2"><Star size={14} /> Avis à modérer</span><b className="font-mono">{stats.reviews_pending}</b></button></li>
              <li><button onClick={() => goTo("stock")} className="flex w-full items-center justify-between hover:text-[#C85A32]"><span className="flex items-center gap-2"><AlertTriangle size={14} /> Stock bas (≤ {stats.low_stock_threshold})</span><b className="font-mono">{stats.low_stock.length}</b></button></li>
              <li className="flex items-center justify-between text-stone-500"><span className="flex items-center gap-2"><RefreshCcw size={14} /> Abonnements souscrits</span><b className="font-mono">{stats.subscriptions_active}</b></li>
            </ul>
          </Card>
          {stats.low_stock.length > 0 && (
            <Card className="p-5 border-[#C85A32]/40 bg-[#FDF6F0]" data-testid="low-stock-card">
              <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#C85A32] mb-2">Alerte stock</p>
              <ul className="text-sm text-stone-700 space-y-1">
                {stats.low_stock.map((pid) => <li key={pid} className="flex justify-between"><span>{PRODUCT_NAMES[pid] || pid}</span><span className="font-mono">{stats.stock[pid]}</span></li>)}
              </ul>
            </Card>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <SectionTitle>Dernières commandes</SectionTitle>
          {stats.recent_orders.length === 0 ? <Empty>Aucune commande.</Empty> : (
            <ul className="divide-y divide-stone-100">
              {stats.recent_orders.map((o) => (
                <li key={o.id} className="py-3 flex items-center justify-between gap-3 text-sm">
                  <div><p className="font-mono text-[#C85A32]">{o.order_number}</p><p className="text-xs text-stone-500">{o.customer_name || o.customer_email || "—"} · {fmtDate(o.created_at)}</p></div>
                  <div className="flex items-center gap-3"><span className="font-mono">{chf(o.total)}</span><StatusBadge status={o.status} /></div>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card className="p-6">
          <SectionTitle>Produits les plus vendus (unités)</SectionTitle>
          {ranking.length === 0 ? <Empty>Pas encore de vente.</Empty> : (
            <ul className="space-y-3">
              {ranking.map(([pid, n]) => (
                <li key={pid} className="text-sm">
                  <div className="flex justify-between mb-1"><span>{PRODUCT_NAMES[pid] || pid}</span><span className="font-mono">{n}</span></div>
                  <div className="h-1.5 rounded-full bg-stone-100"><div className="h-full rounded-full bg-[#4A624E]" style={{ width: `${(n / ranking[0][1]) * 100}%` }} /></div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
};
